// What the driver screens of the ops phone read and decide (05 §5.2 Driver, 04 §4.2 and §4.5):
// my runs, a run's stops, the shipments and pickup requests behind the stops, the boxes out on a
// run and the cash I hold. Everything here is written by the server; the phone changes it only
// through commands (02 §2.7 rule 1). Every query has a scope `where` (driverUid, runId, uid, or one
// document id) and a `limit` (02 §2.3). None needs a composite index: equality and `in` only.
//
// The pure half of the file is what a driver needs to work with no signal: the optimistic overlay
// of the stop and run documents from the device ledger, the checks that run before a command is
// written (a box that is not on this stop, a delivery with neither code nor ID photo) and the
// command payloads. The server stays the authority; these checks only keep the phone honest.
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore';
import { DELIVERY_CODE_PATTERN } from '@ecobox/domain';
import {
  barcodeSchema,
  type PayloadOf,
  type ShipmentDoc,
  type driverCustodyDocSchema,
  type pickupRequestDocSchema,
  type runDocSchema,
  type stopDocSchema,
} from '@ecobox/schema';
import { toDoc, watchRows, type Unsubscribe } from './docs';
import { isWaitingState, type LedgerEntry } from './ledger';
import type { Precheck } from './ops-movement';

export type OpsRun = (typeof runDocSchema)['_output'];
export type OpsStop = (typeof stopDocSchema)['_output'];
export type OpsCustody = (typeof driverCustodyDocSchema)['_output'];
export type OpsPickupRequest = (typeof pickupRequestDocSchema)['_output'];
/** The part of a shipment a delivery stop shows. The rules may let a driver read only some fields. */
export type OpsShipment = ShipmentDoc;
export type StopGeo = NonNullable<OpsStop['geo']>;

/** The boxes a stop is for: the plan's list, and the boxes the stop carries (either may be the one filled in). */
export type StopBoxes = Pick<OpsStop, 'expectedBarcodes' | 'boxes'>;
export function stopBarcodes(stop: StopBoxes): string[] {
  return [...new Set([...stop.expectedBarcodes, ...stop.boxes.map((b) => b.barcode)])];
}

export type StopFailReason = NonNullable<NonNullable<OpsStop['outcome']>['reason']>;

// ---- limits -------------------------------------------------------------------------------

/** A driver has a handful of open runs. */
export const OPS_RUNS_LIMIT = 30;
/** `planRun` carries at most 120 stops and `updateRunStops` can add 120 more. */
export const OPS_STOPS_LIMIT = 250;
/** A run carries at most 200 boxes out (`startRun`). */
export const OPS_RUN_BOXES_LIMIT = 500;
export const OPS_CUSTODY_LIMIT = 10;
/** The most documents followed by id at once (one listener each). */
export const OPS_DOC_WATCH_MAX = 150;

/** Runs that are not finished: the ones a driver still has to work. */
export const OPS_OPEN_RUN_STATUSES = ['planned', 'assigned', 'active'] as const;

const row =
  <T>(idField: string) =>
  (id: string, data: DocumentData): T =>
    toDoc<T>(id, data, idField);

// ---- watchers ----------------------------------------------------------------------------

/**
 * My runs that are not closed yet, past ones included (a run left open yesterday still has to be
 * ended). Scoped by `driverUid == me`. The screens split them into today and upcoming.
 */
export function watchMyOpenRuns(
  db: Firestore,
  uid: string,
  onData: (rows: OpsRun[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'runs'),
    where('driverUid', '==', uid),
    where('status', 'in', [...OPS_OPEN_RUN_STATUSES]),
    limit(OPS_RUNS_LIMIT),
  );
  return watchRows(q, row<OpsRun>('id'), onData, onError);
}

/** My runs that were closed on one date (today's finished runs, so Today can still show them). */
export function watchMyClosedRuns(
  db: Firestore,
  args: { uid: string; date: string },
  onData: (rows: OpsRun[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'runs'),
    where('driverUid', '==', args.uid),
    where('date', '==', args.date),
    where('status', '==', 'closed'),
    limit(OPS_RUNS_LIMIT),
  );
  return watchRows(q, row<OpsRun>('id'), onData, onError);
}

/** The stops of one run, in the planner's order. The run is the scope. */
export function watchRunStops(
  db: Firestore,
  runId: string,
  onData: (rows: OpsStop[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'runs', runId, 'stops'),
    orderBy('sequence', 'asc'),
    limit(OPS_STOPS_LIMIT),
  );
  return watchRows(q, row<OpsStop>('id'), onData, onError);
}

/**
 * The boxes on my stops: the planned ones until the run closes (`plannedDriverUid == me`, the
 * field the rules let a driver query by, 07 §7.3). A box carries `runId` once it is out for
 * delivery, so the screens pick "what should be on my van" from these by run.
 */
export function watchMyBoxes(
  db: Firestore,
  uid: string,
  onData: (rows: RunBox[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'boxes'),
    where('plannedDriverUid', '==', uid),
    limit(OPS_RUN_BOXES_LIMIT),
  );
  return watchRows(
    q,
    (id, data) => {
      const box = toDoc<Record<string, unknown>>(id, data, 'barcode');
      return {
        barcode: String(box['barcode'] ?? id),
        status: typeof box['status'] === 'string' ? box['status'] : '',
        trackingNo: typeof box['trackingNo'] === 'string' ? box['trackingNo'] : '',
        ...(typeof box['runId'] === 'string' ? { runId: box['runId'] } : {}),
      };
    },
    onData,
    onError,
  );
}

export interface RunBox {
  barcode: string;
  status: string;
  trackingNo: string;
  /** The run the box is out on, once it is out for delivery. */
  runId?: string;
}

/** The cash I hold, one document per branch I have collected at. Scoped by `uid == me`. */
export function watchMyCustody(
  db: Firestore,
  uid: string,
  onData: (rows: OpsCustody[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'driverCustody'),
    where('uid', '==', uid),
    limit(OPS_CUSTODY_LIMIT),
  );
  return watchRows(q, row<OpsCustody>('id'), onData, onError);
}

/**
 * Follows documents of one collection by id, one listener each (at most 150), and hands over a map
 * once every one has answered. A document the person may not read, or that is not there, is
 * missing from the map (the first such error is reported once). This is how the shipments and the
 * pickup requests behind a run's stops are kept in the Firestore cache for the road.
 */
export function watchDocsById<T>(
  db: Firestore,
  collectionName: 'shipments' | 'pickupRequests',
  ids: readonly string[],
  idField: string,
  onData: (rows: ReadonlyMap<string, T>) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const unique = [...new Set(ids)].slice(0, OPS_DOC_WATCH_MAX);
  const rows = new Map<string, T>();
  const answered = new Set<string>();
  let reported = false;
  const emit = (): void => {
    if (answered.size >= unique.length) onData(new Map(rows));
  };
  if (unique.length === 0) {
    onData(new Map());
    return () => undefined;
  }
  const stops = unique.map((id) =>
    onSnapshot(
      doc(db, collectionName, id),
      (snap) => {
        if (snap.exists()) rows.set(id, toDoc<T>(snap.id, snap.data(), idField));
        else rows.delete(id);
        answered.add(id);
        emit();
      },
      (err) => {
        rows.delete(id);
        answered.add(id);
        if (!reported) {
          reported = true;
          onError(err);
        }
        emit();
      },
    ),
  );
  return () => {
    for (const stop of stops) stop();
  };
}

// ---- dates, labels, links -------------------------------------------------------------------

/** The device's calendar date, `2026-10-15`. Drivers work in the time zone of their phone. */
export function localDate(now: number | Date = Date.now()): string {
  const d = typeof now === 'number' ? new Date(now) : now;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export const STOP_FAIL_LABELS: Record<StopFailReason, string> = {
  not_home: 'Nobody was there',
  wrong_address: 'Wrong address',
  refused: 'They refused',
  damaged: 'A box is damaged',
  unreachable: 'Could not reach them',
  other: 'Something else',
};

export const RUN_TYPE_LABELS: Record<OpsRun['type'], string> = {
  pickup: 'Pickups',
  delivery: 'Deliveries',
  mixed: 'Pickups and deliveries',
};

/** Who took the delivery. Free text on the command (40 characters). */
export const RECEIVER_RELATIONSHIPS = [
  'The consignee',
  'Family member',
  'Neighbour',
  'Security guard or receptionist',
  'Other',
] as const;

/** A maps link that opens the phone's maps app. The coordinates win; the address is the fallback. */
export function navigateUrl(args: {
  geo?: StopGeo | undefined;
  address?: string | undefined;
}): string | null {
  const { geo, address } = args;
  if (geo) {
    return `https://www.google.com/maps/dir/?api=1&destination=${geo.lat},${geo.lng}&travelmode=driving`;
  }
  const text = address?.trim();
  return text
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(text)}&travelmode=driving`
    : null;
}

/** `tel:` link for a number in international form. */
export function telUrl(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

export function formatAddress(
  address:
    | {
        line1?: string;
        line2?: string;
        barangay?: string;
        city?: string;
        province?: string;
        landmark?: string;
      }
    | undefined,
): string {
  if (!address) return '';
  return [address.line1, address.line2, address.barangay, address.city, address.province]
    .map((p) => p?.trim())
    .filter((p): p is string => Boolean(p))
    .join(', ');
}

/** A browser position as the `geo` the commands carry. Coordinates are kept to about 10 cm. */
export function positionToGeo(position: {
  coords: { latitude: number; longitude: number; accuracy?: number | null };
}): StopGeo | undefined {
  const { latitude, longitude, accuracy } = position.coords;
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180
  ) {
    return undefined;
  }
  const round = (n: number): number => Math.round(n * 1e6) / 1e6;
  return {
    lat: round(latitude),
    lng: round(longitude),
    ...(typeof accuracy === 'number' && Number.isFinite(accuracy) && accuracy >= 0
      ? { accuracyM: Math.min(Math.round(accuracy), 100_000) }
      : {}),
  };
}

// ---- the optimistic overlay ------------------------------------------------------------------

/** Final-mile commands that move a stop or a run; the overlay and the "waiting" marks read these. */
const STOP_COMMANDS = new Set([
  'arriveStop',
  'deliverStop',
  'failStop',
  'completePickup',
  'failPickup',
]);
const RUN_COMMANDS = new Set([...STOP_COMMANDS, 'startRun', 'closeRun']);

export interface StopView extends OpsStop {
  /** The status comes from a command still on this phone or on its way, not from the server yet. */
  waiting: boolean;
}

const STOP_RANK: Record<OpsStop['status'], number> = {
  pending: 0,
  arrived: 1,
  done: 2,
  failed: 2,
  skipped: 2,
};

function payloadStr(entry: LedgerEntry, key: string): string | undefined {
  const v = entry.payload[key];
  return typeof v === 'string' ? v : undefined;
}

function emptyOutcome(): NonNullable<OpsStop['outcome']> {
  return {
    receiverName: '',
    relationship: '',
    photos: [],
    signaturePath: '',
    note: '',
    collectedMinor: 0,
    deliveredBarcodes: [],
    byOffice: false,
  };
}

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function cashOfPayments(v: unknown): number {
  if (!Array.isArray(v)) return 0;
  let sum = 0;
  for (const p of v as unknown[]) {
    if (typeof p !== 'object' || p === null) continue;
    const { method, amountMinor } = p as { method?: unknown; amountMinor?: unknown };
    if (method === 'cash' && typeof amountMinor === 'number') sum += amountMinor;
  }
  return sum;
}

/**
 * One stop as it will be once the commands waiting on this phone are applied (02 §2.2). A stop
 * the server has finished never goes backwards. Rejected commands change nothing: the stop shows
 * the server's status and the screen shows why the command was refused.
 */
export function overlayStop(stop: OpsStop, entries: readonly LedgerEntry[]): StopView {
  let view: StopView = { ...stop, waiting: false };
  for (const e of entries) {
    if (!isWaitingState(e.state) || !STOP_COMMANDS.has(e.type)) continue;
    if (payloadStr(e, 'stopId') !== stop.id || payloadStr(e, 'runId') !== stop.runId) continue;
    const rank = STOP_RANK[view.status];
    switch (e.type) {
      case 'arriveStop':
        if (rank < 1) {
          view = { ...view, status: 'arrived', waiting: true, arrivedAt: e.command.occurredAt };
        }
        break;
      case 'deliverStop':
        if (rank < 2) {
          view = {
            ...view,
            status: 'done',
            waiting: true,
            doneAt: e.command.occurredAt,
            outcome: {
              ...(view.outcome ?? emptyOutcome()),
              receiverName: payloadStr(e, 'receiverName') ?? '',
              relationship: payloadStr(e, 'relationship') ?? '',
              deliveredBarcodes: stringList(e.payload['barcodes']),
              collectedMinor:
                typeof e.payload['collectedMinor'] === 'number' ? e.payload['collectedMinor'] : 0,
            },
          };
        }
        break;
      case 'completePickup':
        if (rank < 2) {
          const cargo = e.payload['cargo'];
          const cargoCash =
            typeof cargo === 'object' && cargo !== null
              ? cashOfPayments((cargo as { payments?: unknown }).payments)
              : 0;
          const dropCash =
            typeof e.payload['emptyBoxCashMinor'] === 'number' ? e.payload['emptyBoxCashMinor'] : 0;
          view = {
            ...view,
            status: 'done',
            waiting: true,
            doneAt: e.command.occurredAt,
            outcome: { ...(view.outcome ?? emptyOutcome()), collectedMinor: cargoCash + dropCash },
          };
        }
        break;
      case 'failStop':
      case 'failPickup':
        if (rank < 2) {
          const reason = payloadStr(e, 'reason') as StopFailReason | undefined;
          view = {
            ...view,
            status: 'failed',
            waiting: true,
            doneAt: e.command.occurredAt,
            outcome: {
              ...(view.outcome ?? emptyOutcome()),
              ...(reason ? { reason } : {}),
              note: payloadStr(e, 'note') ?? '',
            },
          };
        }
        break;
    }
  }
  return view;
}

export function overlayStops(
  stops: readonly OpsStop[],
  entries: readonly LedgerEntry[],
): StopView[] {
  return [...stops].sort((a, b) => a.sequence - b.sequence).map((s) => overlayStop(s, entries));
}

export interface RunView extends OpsRun {
  waiting: boolean;
}

const RUN_RANK: Record<OpsRun['status'], number> = {
  planned: 0,
  assigned: 0,
  active: 1,
  closed: 2,
};

/** A run as it will be once `startRun` / `closeRun` on this phone are applied. */
export function overlayRun(run: OpsRun, entries: readonly LedgerEntry[]): RunView {
  let view: RunView = { ...run, waiting: false };
  for (const e of entries) {
    if (!isWaitingState(e.state) || payloadStr(e, 'runId') !== run.id) continue;
    if (e.type === 'startRun' && RUN_RANK[view.status] < 1) {
      view = { ...view, status: 'active', waiting: true, startedAt: e.command.occurredAt };
    } else if (e.type === 'closeRun' && RUN_RANK[view.status] < 2) {
      view = { ...view, status: 'closed', waiting: true, closedAt: e.command.occurredAt };
    }
  }
  return view;
}

export interface CommandProblem {
  commandId: string;
  type: string;
  stopId?: string;
  state: 'rejected' | 'not_delivered';
  message: string;
}

/**
 * Commands of this run that the server refused, or that never reached it. Nothing disappears
 * silently (02 §2.2): the run and stop screens list them with the reason.
 */
export function runProblems(runId: string, entries: readonly LedgerEntry[]): CommandProblem[] {
  const out: CommandProblem[] = [];
  for (const e of entries) {
    if (!RUN_COMMANDS.has(e.type) || payloadStr(e, 'runId') !== runId) continue;
    if (e.dismissed === true) continue;
    if (e.state !== 'rejected' && e.state !== 'not_delivered') continue;
    const stopId = payloadStr(e, 'stopId');
    out.push({
      commandId: e.id,
      type: e.type,
      ...(stopId ? { stopId } : {}),
      state: e.state,
      message:
        e.lastError?.message ??
        (e.state === 'rejected'
          ? 'The server refused this.'
          : 'The server has not received this yet. Check the connection or resend.'),
    });
  }
  return out;
}

export const COMMAND_LABELS: Record<string, string> = {
  startRun: 'Start the run',
  arriveStop: 'Arrived at a stop',
  deliverStop: 'Delivery',
  failStop: 'Failed delivery',
  completePickup: 'Pickup',
  failPickup: 'Failed pickup',
  closeRun: 'End the run',
};

// ---- progress ------------------------------------------------------------------------------

export interface RunProgress {
  total: number;
  done: number;
  failed: number;
  left: number;
  /** 0 to 100, stops that are finished either way. */
  percent: number;
}

export function runProgress(
  run: Pick<OpsRun, 'counts'>,
  stops: readonly Pick<OpsStop, 'status'>[] | undefined,
): RunProgress {
  const total = stops && stops.length > 0 ? stops.length : run.counts.stops;
  const done =
    stops && stops.length > 0 ? stops.filter((s) => s.status === 'done').length : run.counts.done;
  const failed =
    stops && stops.length > 0
      ? stops.filter((s) => s.status === 'failed').length
      : run.counts.failed;
  const skipped =
    stops && stops.length > 0 ? stops.filter((s) => s.status === 'skipped').length : 0;
  const left = Math.max(0, total - done - failed - skipped);
  return {
    total,
    done,
    failed,
    left,
    percent: total === 0 ? 0 : Math.round(((done + failed + skipped) / total) * 100),
  };
}

export function isStopOpen(status: OpsStop['status']): boolean {
  return status === 'pending' || status === 'arrived';
}

/** Open runs split into those for today or earlier (work now) and those still to come. */
export function splitRuns<T extends Pick<OpsRun, 'date' | 'status' | 'id'>>(
  runs: readonly T[],
  today: string,
): { now: T[]; upcoming: T[] } {
  const sorted = [...runs].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      Number(b.status === 'active') - Number(a.status === 'active') ||
      a.id.localeCompare(b.id),
  );
  return {
    now: sorted.filter((r) => r.date <= today),
    upcoming: sorted.filter((r) => r.date > today),
  };
}

// ---- starting the run: the load scan -----------------------------------------------------------

/** Barcodes the plan says go on this run, with the stop each belongs to. Finished stops drop out. */
export function plannedBoxes(
  stops: readonly Pick<OpsStop, 'id' | 'kind' | 'status' | 'expectedBarcodes' | 'boxes'>[],
): Map<string, string> {
  const out = new Map<string, string>();
  for (const s of stops) {
    if (s.kind !== 'delivery' || !isStopOpen(s.status)) continue;
    for (const b of stopBarcodes(s)) out.set(b, s.id);
  }
  return out;
}

/** The load scan's warning for one box: planned boxes pass, anything else is held back. */
export function precheckRunLoad(args: {
  barcode: string;
  planned: ReadonlyMap<string, string>;
}): Precheck {
  if (args.planned.size === 0) return { level: 'ok', message: '' };
  if (args.planned.has(args.barcode)) return { level: 'ok', message: '' };
  return {
    level: 'refuse',
    message: 'This box is not planned on this run. The server may refuse it.',
    code: 'not_here',
  };
}

export interface UnscannedPlanned {
  stopId: string;
  label: string;
  barcodes: string[];
}

/** Planned boxes the load scan did not see, by stop: they stay at the branch (04 §4.5 `startRun`). */
export function unscannedPlanned(
  stops: readonly Pick<
    OpsStop,
    'id' | 'kind' | 'status' | 'label' | 'expectedBarcodes' | 'boxes'
  >[],
  scanned: ReadonlySet<string>,
): UnscannedPlanned[] {
  const out: UnscannedPlanned[] = [];
  for (const s of stops) {
    if (s.kind !== 'delivery' || !isStopOpen(s.status)) continue;
    const missing = stopBarcodes(s).filter((b) => !scanned.has(b));
    if (missing.length > 0) out.push({ stopId: s.id, label: s.label, barcodes: missing });
  }
  return out;
}

/** A run with no delivery stop has nothing to load: it starts with an empty list (04 §4.5). */
export function needsLoadScan(
  stops: readonly Pick<OpsStop, 'kind' | 'expectedBarcodes' | 'boxes'>[],
): boolean {
  return stops.some((s) => s.kind === 'delivery' && stopBarcodes(s).length > 0);
}

// ---- a delivery stop ----------------------------------------------------------------------------

export type StopBoxCheck =
  { ok: true; barcode: string } | { ok: false; barcode: string; reason: string };

/** A scanned box: a real EcoBox barcode, on this stop, not scanned twice. Refused here and again by the server. */
export function checkStopBarcode(
  stop: StopBoxes,
  scanned: readonly string[],
  raw: string,
): StopBoxCheck {
  const barcode = raw.trim().toUpperCase();
  if (!barcodeSchema.safeParse(barcode).success) {
    return { ok: false, barcode, reason: 'This is not an EcoBox box barcode.' };
  }
  if (scanned.includes(barcode)) {
    return { ok: false, barcode, reason: 'Already scanned for this stop.' };
  }
  if (!stopBarcodes(stop).includes(barcode)) {
    return {
      ok: false,
      barcode,
      reason: 'This box is not for this stop. Keep it on the van.',
    };
  }
  return { ok: true, barcode };
}

export interface DeliveryDraft {
  barcodes: readonly string[];
  receiverName: string;
  relationship: string;
  /** The consignee read out the 4-digit code. When false, an ID photo is needed instead. */
  hasCode: boolean;
  code: string;
  idPhotoCount: number;
  photoCount: number;
  signed: boolean;
  collectedMinor: number | undefined;
}

/** What is missing before a delivery can be saved, in words a driver understands. */
export function deliveryProblems(
  stop: StopBoxes & Pick<OpsStop, 'collectMinor'>,
  draft: DeliveryDraft,
): string[] {
  const out: string[] = [];
  if (draft.barcodes.length === 0) out.push('Scan at least one box for this stop.');
  const own = stopBarcodes(stop);
  const foreign = draft.barcodes.filter((b) => !own.includes(b));
  if (foreign.length > 0) out.push(`${foreign.length} scanned box(es) are not for this stop.`);
  if (draft.hasCode) {
    if (!DELIVERY_CODE_PATTERN.test(draft.code)) {
      out.push('Enter the 4-digit code the consignee received.');
    }
  } else if (draft.idPhotoCount === 0) {
    out.push('With no code, take a photo of the receiver’s ID.');
  }
  if (draft.receiverName.trim() === '') out.push('Write the name of the person who received.');
  if (draft.receiverName.trim().length > 80) out.push('The receiver’s name is too long.');
  if (!draft.signed && draft.photoCount === 0) {
    out.push('Get a signature, or take a photo of the delivered boxes.');
  }
  if (draft.collectedMinor === undefined && stop.collectMinor > 0) {
    out.push('Enter the amount you collected (0 if nothing).');
  }
  return out;
}

/** Warnings that do not block a delivery: boxes left on the van, money short. */
export function deliveryWarnings(
  stop: StopBoxes & Pick<OpsStop, 'collectMinor'>,
  draft: Pick<DeliveryDraft, 'barcodes' | 'collectedMinor'>,
): string[] {
  const out: string[] = [];
  const all = stopBarcodes(stop);
  const left = all.filter((b) => !draft.barcodes.includes(b));
  if (left.length > 0 && draft.barcodes.length > 0) {
    out.push(
      `${left.length} of ${all.length} boxes for this stop are not scanned. They stay on your van and come back at the end of the run.`,
    );
  }
  if (draft.collectedMinor !== undefined && draft.collectedMinor < stop.collectMinor) {
    out.push('You collected less than the amount due. The office will see the difference.');
  }
  return out;
}

export function buildArrivePayload(args: {
  runId: string;
  stopId: string;
  at: string;
  geo?: StopGeo | undefined;
  notifyCustomer: boolean;
}): PayloadOf<'arriveStop'> {
  return {
    runId: args.runId,
    stopId: args.stopId,
    at: args.at,
    ...(args.geo ? { geo: args.geo } : {}),
    notifyCustomer: args.notifyCustomer,
  };
}

export function buildDeliverPayload(args: {
  runId: string;
  stopId: string;
  draft: DeliveryDraft;
  photoPaths: readonly string[];
  idPhotoPath?: string | undefined;
  signaturePath?: string | undefined;
  geo?: StopGeo | undefined;
  method: 'cash' | 'transfer';
}): PayloadOf<'deliverStop'> {
  const { draft } = args;
  return {
    runId: args.runId,
    stopId: args.stopId,
    barcodes: [...draft.barcodes],
    receiverName: draft.receiverName.trim(),
    relationship: draft.relationship.trim().slice(0, 40),
    ...(draft.hasCode ? { deliveryCode: draft.code } : {}),
    ...(!draft.hasCode && args.idPhotoPath ? { idPhotoPath: args.idPhotoPath } : {}),
    photos: [...args.photoPaths],
    signaturePath: args.signaturePath ?? '',
    ...(args.geo ? { geo: args.geo } : {}),
    collectedMinor: draft.collectedMinor ?? 0,
    collectedMethod: args.method,
  };
}

export interface FailureDraft {
  reason: StopFailReason | '';
  note: string;
  photoCount: number;
}

export function failureProblems(draft: FailureDraft): string[] {
  const out: string[] = [];
  if (draft.reason === '') out.push('Choose what happened.');
  if (draft.reason === 'other' && draft.note.trim().length < 3) {
    out.push('Say what happened in the note.');
  }
  if (draft.photoCount === 0 && draft.note.trim().length < 3) {
    out.push('Take a photo of the door or the address, or write a note.');
  }
  return out;
}

export function buildFailPayload(args: {
  type: 'failStop' | 'failPickup';
  runId: string;
  stopId: string;
  draft: FailureDraft;
  photoPaths: readonly string[];
  geo?: StopGeo | undefined;
}): PayloadOf<'failStop'> {
  return {
    runId: args.runId,
    stopId: args.stopId,
    reason: args.draft.reason === '' ? 'other' : args.draft.reason,
    note: args.draft.note.trim().slice(0, 500),
    photos: [...args.photoPaths].slice(0, 5),
    ...(args.geo ? { geo: args.geo } : {}),
  };
}

// ---- ending the run: the scan-back and the cash ----------------------------------------------------

function ledgerBarcodes(
  entries: readonly LedgerEntry[],
  runId: string,
  type: string,
  key: string,
  accept: (e: LedgerEntry) => boolean,
): string[] {
  return entries
    .filter((e) => e.type === type && payloadStr(e, 'runId') === runId && accept(e))
    .flatMap((e) => stringList(e.payload[key]));
}

export interface ExpectedBack {
  /** Boxes that should come back to the branch: loaded and not delivered. */
  barcodes: string[];
  /** Boxes delivered on this run, so a scan of one of them can be flagged. */
  delivered: ReadonlySet<string>;
  /** The load list was not known on this phone: the plan is used instead. */
  fromPlan: boolean;
}

/**
 * What should come back. Loaded = the boxes the server has out on this run, plus the barcodes of
 * this phone's `startRun` (so a run started offline is complete). Delivered = the stops' outcomes
 * and the `deliverStop` commands of this run, applied or waiting. Rejected commands count for
 * nothing. When nothing is known about the load, the plan stands in.
 */
export function expectedBack(args: {
  runId: string;
  stops: readonly StopView[];
  runBoxes: readonly RunBox[];
  entries: readonly LedgerEntry[];
}): ExpectedBack {
  const { runId, stops, runBoxes, entries } = args;
  const live = (e: LedgerEntry): boolean => e.state !== 'rejected';
  const delivered = new Set<string>([
    ...stops.filter((s) => s.status === 'done').flatMap((s) => s.outcome?.deliveredBarcodes ?? []),
    ...ledgerBarcodes(entries, runId, 'deliverStop', 'barcodes', live),
  ]);
  const loaded = new Set<string>([
    ...runBoxes
      .filter((b) => b.status === 'out_for_delivery' && b.runId === runId)
      .map((b) => b.barcode),
    ...ledgerBarcodes(entries, runId, 'startRun', 'barcodes', live),
  ]);
  const fromPlan = loaded.size === 0;
  const base = fromPlan
    ? stops.filter((s) => s.kind === 'delivery').flatMap((s) => stopBarcodes(s))
    : [...loaded];
  return {
    barcodes: [...new Set(base)].filter((b) => !delivered.has(b)).sort(),
    delivered,
    fromPlan,
  };
}

/** A box scanned back: expected ones pass; anything else is still accepted (a scan is a fact) but flagged. */
export function precheckScanBack(args: { barcode: string; expected: ExpectedBack }): Precheck {
  const { barcode, expected } = args;
  if (expected.barcodes.includes(barcode)) return { level: 'ok', message: '' };
  if (expected.delivered.has(barcode)) {
    return {
      level: 'warn',
      message: 'This box was delivered on this run. Is it back by mistake?',
      code: 'extra',
    };
  }
  return {
    level: 'warn',
    message: 'This box is not on this run’s list. It will be flagged for the office.',
    code: 'extra',
  };
}

export interface EndRunSummary {
  expected: number;
  scannedBack: number;
  missing: string[];
  /** Stops still open: pickups and deliveries nobody finished. */
  openStops: number;
}

export function endRunSummary(args: {
  expected: ExpectedBack;
  scanned: ReadonlySet<string>;
  stops: readonly Pick<StopView, 'status'>[];
}): EndRunSummary {
  const missing = args.expected.barcodes.filter((b) => !args.scanned.has(b));
  return {
    expected: args.expected.barcodes.length,
    scannedBack: args.expected.barcodes.length - missing.length,
    missing,
    openStops: args.stops.filter((s) => isStopOpen(s.status)).length,
  };
}

export interface CashSummary {
  currency: string;
  /** Cash taken on this run: what the server has plus what is still on this phone. */
  collectedMinor: number;
  /** The part of it that the server has not seen yet. */
  waitingMinor: number;
  /** Stops where cash was taken, newest information first. */
  lines: { stopId: string; label: string; amountMinor: number }[];
  /** What the server says the driver holds for the cashier (all runs), plus waiting cash. */
  toHandOverMinor: number;
  heldMinor: number;
}

/** Cash taken on a run and what to hand to the cashier. Only cash counts; a transfer is not in the driver's pocket. */
export function cashSummary(args: {
  run: Pick<OpsRun, 'id' | 'branchId' | 'currency' | 'cashCollectedMinor'>;
  stops: readonly StopView[];
  custody: readonly OpsCustody[];
  entries: readonly LedgerEntry[];
}): CashSummary {
  const { run, stops, custody, entries } = args;
  let waitingMinor = 0;
  for (const e of entries) {
    if (!isWaitingState(e.state) || payloadStr(e, 'runId') !== run.id) continue;
    if (e.type === 'deliverStop' && e.payload['collectedMethod'] !== 'transfer') {
      const n = e.payload['collectedMinor'];
      if (typeof n === 'number') waitingMinor += n;
    } else if (e.type === 'completePickup') {
      const cargo = e.payload['cargo'];
      if (typeof cargo === 'object' && cargo !== null) {
        waitingMinor += cashOfPayments((cargo as { payments?: unknown }).payments);
      }
      const drop = e.payload['emptyBoxCashMinor'];
      if (typeof drop === 'number') waitingMinor += drop;
    }
  }
  const heldMinor = custody
    .filter((c) => c.branchId === run.branchId && c.currency === run.currency)
    .reduce((n, c) => n + c.heldMinor, 0);
  return {
    currency: run.currency,
    collectedMinor: run.cashCollectedMinor + waitingMinor,
    waitingMinor,
    lines: stops
      .filter((s) => (s.outcome?.collectedMinor ?? 0) > 0)
      .map((s) => ({ stopId: s.id, label: s.label, amountMinor: s.outcome?.collectedMinor ?? 0 })),
    toHandOverMinor: heldMinor + waitingMinor,
    heldMinor,
  };
}

export function buildCloseRunPayload(args: {
  runId: string;
  barcodesBack: readonly string[];
  note?: string | undefined;
}): PayloadOf<'closeRun'> {
  return {
    runId: args.runId,
    barcodesBack: [...args.barcodesBack],
    note: (args.note ?? '').trim().slice(0, 300),
  };
}
